'use client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';
import { env } from '@/utils/envs';

// --- Types ---

type DiscordVerifyResponse = {
  verified: boolean;
  reason?: string;
  error?: string;
  code?: string;
};

// --- Query Keys ---

export const discordVerificationKeys = {
  all: ['discord-verification'] as const,
  status: (authenticated: boolean) =>
    [...discordVerificationKeys.all, authenticated] as const,
};

// --- Query Hook ---

/**
 * Fetch the cached Discord verified status from the backend.
 */
export const useDiscordVerifiedStatus = () => {
  const { authenticated, ready, getAccessToken } = usePrivy();

  return useQuery<DiscordVerifyResponse>({
    queryKey: discordVerificationKeys.status(authenticated),
    queryFn: async () => {
      if (!authenticated) {
        return { verified: false };
      }

      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token');
      }

      const response = await fetch(
        `${env.VITE_SHINKAI_WEB_API_URL}/api/discord/verify`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch Discord verification status');
      }

      return (await response.json()) as DiscordVerifyResponse;
    },
    enabled: ready && authenticated,
    staleTime: 60000, // Cache for 1 minute
  });
};

// --- Mutation Hook ---

/**
 * Trigger a live Discord verification check.
 * Calls POST /api/discord/verify which checks the Discord API
 * and caches the result in the database.
 *
 * Sends the caller's Privy-sourced linked accounts in the request body so the
 * backend can sync them before looking up the Discord account.  This prevents
 * the "No Discord account linked" error when the dapp hasn't yet synced
 * accounts to the database.
 */
export const useVerifyDiscord = () => {
  const { authenticated, getAccessToken, user } = usePrivy();
  const queryClient = useQueryClient();

  return useMutation<DiscordVerifyResponse, Error>({
    mutationFn: async () => {
      if (!authenticated) {
        throw new Error('Not authenticated');
      }

      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token');
      }

      const response = await fetch(
        `${env.VITE_SHINKAI_WEB_API_URL}/api/discord/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            linkedAccounts: user?.linkedAccounts ?? [],
          }),
        },
      );

      const data = (await response.json()) as DiscordVerifyResponse;

      if (!response.ok) {
        throw new Error(data.error ?? 'Failed to verify Discord');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: discordVerificationKeys.all,
      });
    },
  });
};
